/**
 * Billing Commands
 *
 * Commands dispatched via the YJS pendingCmds mechanism to keep billing data
 * up-to-date across all open tabs in real-time.
 *
 * billing_refresh — triggered by the backend after every AI billing write and by
 * chatStore.onFinally after every stream. Forces a cache-bypassing refetch of
 * the current bill on every connected tab.
 */

import { SelfContainedCommand } from '../../types/commands';
import { StandardToolResult, createSuccessResult } from '../../types/standardToolResults';

const billingRefreshCommand: SelfContainedCommand = {
  id: 'billing_refresh',
  description: 'Force-refresh billing data on this tab (triggered via YJS pendingCmds)',
  autonomyLevel: 'auto',

  schema: { parameters: [] },

  handler: async (): Promise<StandardToolResult> => {
    try {
      const { default: useUserStore } = await import('../../stores/userStore');
      // Do NOT pass force:true here. The active tab (which wrote the pendingCmd) already
      // did a force-refresh directly in chatStore.onFinally and has a fresh billLastFetched.
      // The 60s cache will short-circuit for it; other tabs with stale/no cache will fetch.
      await useUserStore.getState().fetchCurrentBill();
    } catch (err) {
      // Non-fatal — billing refresh should never break the app
      console.warn('[BillingCmd] billing_refresh failed (non-fatal):', err);
    }
    return createSuccessResult('Billing refreshed');
  },
} as SelfContainedCommand;

export const billingCommands: SelfContainedCommand[] = [billingRefreshCommand];
